// src/utils/util.ts
import type { AuthUser } from "@/api/auth";
import { WeeklyOpenSlotItem } from "@/pages/multishop-selection/responses";
import { ShopId } from "@/pages/multishop-selection/types";

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: any[]) => void;
  }
}

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
const PORTAL_TYPE = "PLANNER";
const GA_SCRIPT_ID = 'ga-script-id'
export const GA_EVENT = {
  COMBINATION_SEARCH: "COMBINATION_SEARCH",
  COMBINATION_RESULT_VIEW: "COMBINATION_RESULT_VIEW",
  COMBINATION_SELECT: "COMBINATION_SELECT",
  COMBINATION_TO_SLOT_VIEW: 'COMBINATION_TO_SLOT_VIEW',

  SLOT_VIEW: "SLOT_VIEW",
  SLOT_SELECT: "SLOT_SELECT",

  RESV_CONFIRMATION_VIEW: "RESV_CONFIRMATION_VIEW",
  RESV_SUBMIT: "RESV_SUBMIT",
  RESV_SUCCESS: "RESV_SUCCESS",
  RESV_FAIL: "RESV_FAIL",

  RESV_CANCEL_CLICK: "RESV_CANCEL_CLICK",
  RESV_CANCEL_SUBMIT: "RESV_CANCEL_SUBMIT",
  RESV_CANCEL_SUCCESS: "RESV_CANCEL_SUCCESS",
  RESV_CANCEL_FAIL: "RESV_CANCEL_FAIL",
} as const;

export const DIRECT_SLOT = "DIRECT_SLOT"
export const COMBINATION = "COMBINATION" 
export type BookingPath = "COMBINATION" | "DIRECT_SLOT";

type GAEventName = (typeof GA_EVENT)[keyof typeof GA_EVENT];

type CommonEventParams = {
  booking_path?: BookingPath;
  reservation_code?: string;
  slot_count?: number;
  shop_count?: number;
  selected_date?: string;
  fail_reason?: string;
  cancel_reason_code?: string;
  page_name?: string;
};

let isInitialized = true;

function isAnalyticsEnabled() {
  
  return Boolean(GA_MEASUREMENT_ID) && typeof window !== "undefined";
}

function cleanParams<T extends Record<string, any>>(params: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== ""
    )
  ) as Partial<T>;
}

function ensureGtagStub() {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    window.gtag = function (...args: any[]) {
      window.dataLayer.push(args);
    };
  }
}

function injectGtagScript() {
  if (document.getElementById(GA_SCRIPT_ID)) return;

  const script = document.createElement("script");
  script.id = GA_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
}

function gtag(...args: any[]) {
  if (!isAnalyticsEnabled()) return;
  ensureGtagStub();
  window.gtag?.(...args);
}

export function initAnalytics() {
  if (!isAnalyticsEnabled()) return;
  if (isInitialized) return;

  ensureGtagStub();
  injectGtagScript();

  gtag("js", new Date());
  gtag("config", GA_MEASUREMENT_ID, {
    send_page_view: false,
  });

  isInitialized = true;
}

export function setAnalyticsUser(user: AuthUser | null) {
  if (!isAnalyticsEnabled()) return;
  console.log(user)
  if (!user) {
    gtag("config", GA_MEASUREMENT_ID, {
      user_id: undefined,
    });

    gtag("set", "user_properties", {
      portal_type: PORTAL_TYPE,
      user_type: undefined,
    });
    return;
  }

  gtag("config", GA_MEASUREMENT_ID, {
    user_id: String(user.id),
  });

  gtag("set", "user_properties", cleanParams({
    portal_type: PORTAL_TYPE,
    user_type: user.userType,
  }));
}

export function clearAnalyticsUser() {
  setAnalyticsUser(null);
}

export function sendPageView(params?: {
  pathname?: string;
  search?: string;
  title?: string;
}) {
  if (!isAnalyticsEnabled()) return;

  const pathname = params?.pathname ?? window.location.pathname;
  const search = params?.search ?? window.location.search;
  const pagePath = `${pathname}${search || ""}`;
  const pageLocation =
    typeof window !== "undefined"
      ? `${window.location.origin}${pagePath}`
      : pagePath;

  gtag("event", "page_view", cleanParams({
    page_title: params?.title ?? document.title,
    page_path: pagePath,
    page_location: pageLocation,
    portal_type: PORTAL_TYPE,
  }));
}

export function trackEvent(
  eventName: GAEventName,
  params?: CommonEventParams & Record<string, any>
) {
  if (!isAnalyticsEnabled()) return;

  gtag(
    "event",
    eventName,
    cleanParams({
      ...params,
    })
  );
}

export function trackBookingSuccess(params: {
  booking_path: BookingPath;
  reservation_code?: string;
  slot_count?: number;
  shop_count?: number;
  selected_date?: string;
}) {
  trackEvent(GA_EVENT.RESV_SUCCESS, params);
}

export function trackBookingFail(params: {
  booking_path: BookingPath;
  slot_count?: number;
  shop_count?: number;
  selected_date?: string;
  fail_reason?: string;
}) {
  trackEvent(GA_EVENT.RESV_FAIL, params);
}

export function trackBookingCancelSuccess(params: {
  booking_path: BookingPath;
  reservation_code?: string;
  slot_count?: number;
  shop_count?: number;
  cancel_reason_code?: string;
}) {
  trackEvent(GA_EVENT.RESV_CANCEL_SUCCESS, params);
}

export function trackBookingCancelFail(params: {
  booking_path: BookingPath;
  reservation_code?: string;
  slot_count?: number;
  shop_count?: number;
  cancel_reason_code?: string;
  fail_reason?: string;
}) {
  trackEvent(GA_EVENT.RESV_CANCEL_FAIL, params);
}


export function buildDirectSlotSelectAnalyticsPayload({
  selectedRows,
  selectedShopIds,
}: {
  selectedRows: WeeklyOpenSlotItem[];
  selectedShopIds: ShopId[];
}) {
  const sortedRows = [...selectedRows].sort(
    (a, b) => a.startMinuteOfDay - b.startMinuteOfDay
  );

  const selectedDate = sortedRows[0]?.slotDate || null;

  const payload: Record<string, any> = {
    booking_path: GA_EVENT.SLOT_VIEW,
    shop_count: selectedShopIds.length,
    slot_count: sortedRows.length,
    selected_date: selectedDate,
    // days_until_visit: selectedDate ? getDaysUntilVisit(selectedDate) : undefined,
    // days_until_visit_bucket: selectedDate
    //   ? getDaysUntilVisitBucket(selectedDate)
    //   : undefined,
  };

  sortedRows.forEach((slot, index) => {
    const order = index + 1;
    payload[`shop_${order}_id`] = slot.shopId;
    payload[`slot_${order}_id`] = slot.id;
    payload[`start_${order}_minute`] = slot.startMinuteOfDay;
    payload[`start_${order}_time_code`] = slot.startTimeCode;
  });

  return payload;
}